import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../ui/buttons/AppButton";
import { onNewerBuild, servedBuildIsNewer } from "./appBuild";

/** How often an open tab looks for a newer build while it is shown. */
const BUILD_CHECK_INTERVAL_MILLISECONDS = 5 * 60_000;
/** The shortest time between two looks for focus or visibility (F164: no check more often than about once a minute). */
const LOOK_AGAIN_AFTER_MILLISECONDS = 60_000;

/**
 * One quiet line under the top bar once a newer build of the app is served (F89, F164 item 3): "DMW wurde
 * aktualisiert" and "Neu laden". The tab looks when it is shown again, at most once a minute, and every few minutes;
 * the API client tells it when an answer could not be read, and the live socket when it came back after a drop, as
 * after a deploy (`ThreadLiveProvider`).
 */
export default function NewerBuildNotice() {
  const { t } = useTranslation();
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    let lastLook = Number.NEGATIVE_INFINITY;
    const look = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastLook < LOOK_AGAIN_AFTER_MILLISECONDS) return;
      lastLook = now;
      void servedBuildIsNewer().then((found) => { if (found) setNewer(true); });
    };
    const stopListening = onNewerBuild(() => { setNewer(true); });
    const interval = window.setInterval(look, BUILD_CHECK_INTERVAL_MILLISECONDS);
    window.addEventListener("focus", look);
    document.addEventListener("visibilitychange", look);
    look();
    return () => {
      stopListening();
      window.clearInterval(interval);
      window.removeEventListener("focus", look);
      document.removeEventListener("visibilitychange", look);
    };
  }, []);

  if (!newer) return null;
  return (
    <div role="status" className="app-status-info flex shrink-0 items-center justify-center gap-3 px-4 py-1 text-sm">
      <span>{t("errors.appOutdated.title")}</span>
      <AppButton variant="secondary" size="xs" onClick={() => { window.location.reload(); }}>{t("connection.reload")}</AppButton>
    </div>
  );
}
